import React, { useCallback, useContext, useEffect, useState } from "react";

import { ZeosApiContext } from "../../../context/ZeosApiContext";
import {
  IMeteringAttestationContract,
  IServiceProfileContract,
  METERING_ATTESTATION_STATUS,
  METERING_PROFILE,
  METERING_PROFILE_OPTIONS,
  profileTakesMeterCount,
} from "../../../services/zeos-api/contracts/Property";

// MTR-P0-04 steward queue (task-4048, spec v0.3). Rendered as an Admin panel tab, which is
// already super-admin only; the API enforces the same rule. One row per service that is
// unanswered or answered "Not sure"; the steward records each service once determined.

const isSuccess = (status?: string) => status?.toLowerCase() === "success";

const STATUS_LABELS: Record<string, string> = {
  [METERING_ATTESTATION_STATUS.AwaitingAnswer]: "Awaiting answer",
  [METERING_ATTESTATION_STATUS.AnsweredNotSure]: "Answered: not sure",
};

// "Not sure" is the customer's escape hatch, not a steward resolution.
const STEWARD_OPTIONS = METERING_PROFILE_OPTIONS.filter((o) => o.value !== METERING_PROFILE.Unknown);

interface IQueueRow {
  property: IMeteringAttestationContract;
  service: IServiceProfileContract;
  key: string;
}

interface IRowDraft {
  profile: string;
  count: string;
}

const isValidCount = (count: string) => count.trim() === "" || /^[1-9]\d*$/.test(count.trim());

export const MeteringAttestationQueue = () => {
  const api = useContext(ZeosApiContext);
  const [queue, setQueue] = useState<IMeteringAttestationContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [drafts, setDrafts] = useState<Record<string, IRowDraft>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.Property.meteringAttestationStewardQueueAsync();
      if (isSuccess(response.zeosStatus) && Array.isArray(response.data)) {
        setQueue(response.data);
      } else {
        setError(response.message || "Could not load the steward queue.");
      }
    } catch {
      setError("Could not load the steward queue.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const rows: IQueueRow[] = queue.flatMap((property) =>
    (property.services ?? [])
      .filter((s) => s.attestationStatus !== METERING_ATTESTATION_STATUS.Confirmed)
      .map((service) => ({ property, service, key: `${property.propertyId}-${service.utilityTypeId}` }))
  );

  const draftFor = (key: string): IRowDraft => drafts[key] ?? { profile: "", count: "" };

  const updateDraft = (key: string, change: Partial<IRowDraft>) => {
    setDrafts((all) => ({ ...all, [key]: { ...draftFor(key), ...change } }));
  };

  const canSave = (key: string) => {
    const draft = draftFor(key);
    return draft.profile !== "" && (!profileTakesMeterCount(draft.profile) || isValidCount(draft.count));
  };

  const saveRow = async (row: IQueueRow) => {
    if (!canSave(row.key)) {
      return;
    }
    const draft = draftFor(row.key);
    const count = profileTakesMeterCount(draft.profile) ? draft.count.trim() : "";
    setSavingKey(row.key);
    setError("");
    try {
      const response = await api.Property.submitMeteringAttestationAsync(row.property.propertyId, [
        {
          utilityTypeId: row.service.utilityTypeId,
          meteringProfile: draft.profile,
          expectedMeterCount: count === "" ? null : Number(count),
        },
      ]);
      if (isSuccess(response.zeosStatus)) {
        setDrafts((all) => {
          const { [row.key]: _saved, ...rest } = all;
          return rest;
        });
        await loadQueue();
      } else {
        setError(response.message || `Could not save ${row.property.propertyName}.`);
      }
    } catch {
      setError(`Could not save ${row.property.propertyName}.`);
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded p-4">
      <h3 className="text-sm font-semibold text-black mb-1">
        Metering attestation queue
      </h3>
      <p className="text-xs text-gray-600 mb-3">
        Services whose metering has not been confirmed, one row per property and service.
        Customer-facing coverage caveats stay suppressed until a service is answered.
      </p>
      {error && (
        <p role="alert" className="text-xs text-red-600 mb-3">
          {error}
        </p>
      )}
      {loading && <p className="text-sm text-gray-600">Loading queue…</p>}
      {!loading && rows.length === 0 && !error && (
        <p className="text-sm text-gray-600">Nothing waiting. Every service is confirmed.</p>
      )}
      {!loading && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-gray-200">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="px-3 py-2 border-b">Company</th>
                <th className="px-3 py-2 border-b">Property</th>
                <th className="px-3 py-2 border-b">Location</th>
                <th className="px-3 py-2 border-b">Service</th>
                <th className="px-3 py-2 border-b">Meters</th>
                <th className="px-3 py-2 border-b">Status</th>
                <th className="px-3 py-2 border-b">Set profile</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const draft = draftFor(row.key);
                const name = `${row.property.propertyName} ${row.service.serviceName}`;
                return (
                  <tr key={row.key} className="border-t">
                    <td className="px-3 py-2">{row.property.companyName}</td>
                    <td className="px-3 py-2">{row.property.propertyName}</td>
                    <td className="px-3 py-2">
                      {[row.property.city, row.property.state].filter(Boolean).join(", ")}
                    </td>
                    <td className="px-3 py-2">{row.service.serviceName}</td>
                    <td className="px-3 py-2">{row.service.liveMeterCount}</td>
                    <td className="px-3 py-2">
                      {STATUS_LABELS[row.service.attestationStatus] ?? row.service.attestationStatus}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <select
                          aria-label={`Metering profile for ${name}`}
                          className="text-xs border border-gray-300 rounded px-1 py-1"
                          value={draft.profile}
                          onChange={(e) => updateDraft(row.key, { profile: e.target.value })}
                          disabled={savingKey !== null}
                        >
                          <option value="">Choose…</option>
                          {STEWARD_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                        {profileTakesMeterCount(draft.profile) && (
                          <input
                            type="number"
                            min={1}
                            step={1}
                            aria-label={`Meter count for ${name}`}
                            placeholder="Meters"
                            className="w-20 text-xs border border-gray-300 rounded px-1 py-1"
                            value={draft.count}
                            onChange={(e) => updateDraft(row.key, { count: e.target.value })}
                            disabled={savingKey !== null}
                            aria-invalid={!isValidCount(draft.count)}
                          />
                        )}
                        <button
                          type="button"
                          aria-label={`Save ${name}`}
                          className="text-xs text-zeos-orange-dk hover:underline disabled:opacity-50"
                          onClick={() => saveRow(row)}
                          disabled={savingKey !== null || !canSave(row.key)}
                        >
                          {savingKey === row.key ? "Saving…" : "Save"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
